// Feature #229: client-side rate-calculator mirror.
//
// The authoritative implementation lives on the server in
// ``backend/app/services/tap_rate_calculator.py``. This module mirrors it
// so the Assessment tab can give users a live preview while they're
// editing the rate inputs — without that, AC #1's "Recalculates live as
// the user changes the rate inputs" would only fire on save+reload.
//
// Keep this in lockstep with the Python service. The unit tests in
// ``tests/test_tap_rate_calculator.py`` are the source of truth for the
// expected outputs.

import type { TapCalculatedRateSet } from '@/types/index'
// TapRateCalculatorSettings is defined alongside its API client; importing
// here keeps the wire shape and the calculator math co-located in their
// respective modules.
import type { TapRateCalculatorSettings as ApiSettings } from '@/api/tapResources'

export type CanonicalEmploymentType = 'FullTime' | 'PartTime' | 'Casual' | 'Contractor'

/** Mirror of canonical_employment_type() in the Python service. */
export const canonicalEmploymentType = (raw: string | undefined | null): CanonicalEmploymentType | null => {
  if (!raw) return null
  const lowered = raw.trim().toLowerCase()
  if (lowered.includes('part time') || lowered.includes('part-time')) return 'PartTime'
  if (lowered.includes('full time') || lowered.includes('full-time') || lowered.includes('fixed term')) return 'FullTime'
  if (lowered.includes('casual')) return 'Casual'
  if (lowered.includes('contract')) return 'Contractor'
  return null
}

// utilisationTargetDays = (weekdays - non-working days) * utilisation %.
// Used by sell/day in the Permanent calculator. Not exported because no
// caller outside this file needs it (the settings page derives its own
// equivalent inline for read-only display).
const utilisationTargetDays = (s: ApiSettings): number => {
  const cap = s.weekdaysPerYear - (s.annualLeaveDays + s.sickLeaveDays + s.professionalDevelopmentDays + s.internalManagementDays)
  return cap * s.utilisationTargetPct
}

/** Permanent (Full Time / Part Time / legacy Fixed Term) calculator.
 *
 * Per the #229 corrective comment, ``costPerHour`` divides by gross
 * weekdays/year (251) — not capacity-after-leave (197). $125k FT salary
 * → $76.23/hr. The buy/sell math is unchanged. */
const calcPermanent = (s: ApiSettings, salary: number, useFullTimeMargins: boolean): TapCalculatedRateSet => {
  const fteLoad = 1.0 // Per spec §1: fixed at 1.0 in the platform.
  const superAmt = salary * s.superRate
  const payrollTax = (salary + superAmt) * s.payrollTaxRate
  const annualPackage = salary + superAmt + payrollTax
  const actualPackage = annualPackage * fteLoad

  const ut = utilisationTargetDays(s)
  const minTgp = useFullTimeMargins ? s.minTgpFullTime : s.minTgpPartTime
  const minSgp = useFullTimeMargins ? s.minSgpFullTime : s.minSgpPartTime

  // Cost denominator = gross availability days × hrs/day, NOT capacity.
  const costPerHour = actualPackage / (s.weekdaysPerYear * s.hoursPerDay)
  const sellPerDay = (actualPackage / (1 - minTgp)) / ut
  const sellPerHour = sellPerDay / s.hoursPerDay
  const buyPerDay = sellPerDay - sellPerDay * minSgp
  const buyPerHour = buyPerDay / s.hoursPerDay

  return {
    costPerHour,
    buyPerHour,
    sellPerHour,
    costPerDay: costPerHour * s.hoursPerDay,
    buyPerDay,
    sellPerDay,
    // Annual dimension dropped per #229 corrective comment.
  }
}

/** Casual calculator. */
const calcCasual = (s: ApiSettings, costRateHourly: number): TapCalculatedRateSet => {
  const costPerHour = (costRateHourly * (1 + s.superRate)) + (costRateHourly * s.payrollTaxRate)
  const sellPerHour = costPerHour / (1 - s.minTgpCasual)
  const buyPerHour = sellPerHour - sellPerHour * s.minSgpCasual
  return {
    costPerHour,
    buyPerHour,
    sellPerHour,
    costPerDay: costPerHour * s.hoursPerDay,
    buyPerDay: buyPerHour * s.hoursPerDay,
    sellPerDay: sellPerHour * s.hoursPerDay,
  }
}

/** Contractor calculator (no super, no payroll tax). */
const calcContractor = (s: ApiSettings, hourlyExGst: number): TapCalculatedRateSet => {
  const costPerHour = hourlyExGst
  const sellPerHour = costPerHour / (1 - s.minTgpContractor)
  const buyPerHour = sellPerHour - sellPerHour * s.minSgpContractor
  return {
    costPerHour,
    buyPerHour,
    sellPerHour,
    costPerDay: costPerHour * s.hoursPerDay,
    buyPerDay: buyPerHour * s.hoursPerDay,
    sellPerDay: sellPerHour * s.hoursPerDay,
  }
}

/** Dispatch wrapper. Returns null if any input is missing or the type
 * can't be canonicalised — caller should treat null as "show inputs only". */
export const calculateRates = (
  settings: ApiSettings | null | undefined,
  employmentType: string | undefined | null,
  rateInput: number | undefined | null,
): TapCalculatedRateSet | null => {
  if (!settings || rateInput == null || !Number.isFinite(rateInput)) return null
  const canonical = canonicalEmploymentType(employmentType)
  if (!canonical) return null
  if (canonical === 'FullTime') return calcPermanent(settings, rateInput, true)
  if (canonical === 'PartTime') return calcPermanent(settings, rateInput, false)
  if (canonical === 'Casual') return calcCasual(settings, rateInput)
  if (canonical === 'Contractor') return calcContractor(settings, rateInput)
  return null
}

/** Per-employment-type metadata for the rate input field on the form. */
export interface RateInputMeta {
  label: string
  unit: string  // suffix shown after the input, e.g. "$ / year"
  placeholder: string
}

export const rateInputMeta = (employmentType: string | undefined | null): RateInputMeta => {
  const canonical = canonicalEmploymentType(employmentType)
  switch (canonical) {
    case 'FullTime':
      return { label: 'Annual Salary (ex Super)', unit: '$ / year', placeholder: 'e.g. 125000' }
    case 'PartTime':
      return { label: 'Annual Salary (ex Super), at agreed FTE', unit: '$ / year', placeholder: 'e.g. 75000' }
    case 'Casual':
      return { label: 'Hourly Cost Rate (ex Super, ex PT)', unit: '$ / hr', placeholder: 'e.g. 50' }
    case 'Contractor':
      return { label: 'Hourly Rate (ex GST)', unit: '$ / hr', placeholder: 'e.g. 120' }
    default:
      return { label: 'Rate', unit: '', placeholder: 'Select an Employment Type first' }
  }
}

/**
 * Per the #229 corrective comment, the Assessment tab shows Cost / Buy /
 * Sell as **per-hour rates for every Employment Type** (FT, PT, Casual,
 * Contractor). Day rate is shown as a secondary block underneath since
 * it falls out of the formulas at no extra cost. Annual is not displayed.
 *
 * These helpers are kept (rather than inlining `true`) so the call sites
 * stay self-documenting; if a future change re-introduces type-specific
 * dimensions, the logic lives in one place.
 */
export const showsHourly = (_employmentType: string | undefined | null): boolean => true
export const showsDay = (_employmentType: string | undefined | null): boolean => true
